import styled from 'styled-components'
import { Image } from '@/features/shared/elements/Image/NextImage'
import { Button } from '@shipt/design-system-buttons'
import { Column, Row, Stack } from '@shipt/design-system-layouts'
import { screenSizes } from '@shipt/design-system-themes'
import { Body, Headline } from '@shipt/design-system-typography'
import { HDivider } from '@/features/shared/components/Dividers'
import { type GiftCardFormInput } from '@/features/cms/components/CMS/components/GiftCardPurchaseForm/GiftCardRecipientForm'
import Modal, { Content, Header } from '@/features/shared/elements/Modals/LargeModal'
import { type DialogProps } from '@/features/shared/state/Dialog/types'
import { GiftPlanType } from '@/features/cms/components/CMS/components/GiftCardPurchaseForm/constants'
import { useEffect, useState } from 'react'
import { trackElementClicked, trackElementViewed } from '@/features/shared/analytics/element'
import { Tile, TileGroup } from '@shipt/design-system-tile'
import { Checkbox } from '@shipt/design-system-checkbox'
import { PositiveIcon } from '@shipt/design-system-icons'
import { routes } from '@shared/constants/routes'
import { LEGAL_LINKS } from '@/features/shared/constants/legalLinks'
import { useSegwayRouter } from '@/features/shared/hooks/useSegwayRouter'
import { useQueryGiftCardChoices } from '@/features/purchase/services/GiftCard/queries'
import { formatCents } from '@/features/shared/utils/formatter'
import { Skeleton } from '@shipt/design-system-loading'
import { theme } from '@/features/shared/constants/shiptTheme'

type Props = {
  formData: GiftCardFormInput
}
export const upsellErrorMessage = (
  <span>
    One of your gift cards is on its way!{' '}
    <strong>However, the gift card for an Annual Membership failed to process.</strong> Please try
    purchasing it again.
  </span>
)
export const GiftCardOrderOverviewModal = ({ onClose, formData }: Props & DialogProps) => {
  const {
    recipientName,
    recipientEmail,
    senderName,
    senderEmail,
    personalMessage,
    giftImage,
    cardChoice,
  } = formData
  const [upsellChecked, setUpsellChecked] = useState(false)
  const router = useSegwayRouter()

  const { data: membershipOptions, isLoading } = useQueryGiftCardChoices()

  const isMembership =
    cardChoice === GiftPlanType.GROCERY_YEARLY || cardChoice === GiftPlanType.GROCERY_SIX_MONTHS

  const backendAnnualOption = membershipOptions?.gift_membership_options.find(
    (option) => option.value === GiftPlanType.GROCERY_YEARLY,
  )

  const showUpsellSkeleton = !isMembership && isLoading
  const showUpsell = !isMembership && !!backendAnnualOption

  const isPromoActive = backendAnnualOption?.is_promo ?? false
  const upsellMembershipCost = backendAnnualOption ? backendAnnualOption.price_cents / 100 : 99

  const annualMembershipPrice = backendAnnualOption
    ? formatCents(backendAnnualOption.original_price_cents)
    : ''
  const promoAnnualMembershipPrice = backendAnnualOption
    ? formatCents(backendAnnualOption.price_cents)
    : ''
  const upsellTotal = parseInt(cardChoice) + upsellMembershipCost

  const renderMembershipPriceCopy = () => {
    if (isPromoActive) {
      return (
        <CheckBoxDescription size="md">
          Make this gift extra special by adding an Annual Membership gift card for{' '}
          {promoAnnualMembershipPrice}{' '}
          <Body as="s" size="md" strike variant="secondary">
            (reg. {annualMembershipPrice})
          </Body>
        </CheckBoxDescription>
      )
    } else {
      return (
        <CheckBoxDescription size="md">
          Make this gift extra special by adding an Annual Membership gift card for{' '}
          {annualMembershipPrice}
        </CheckBoxDescription>
      )
    }
  }
  const MembershipPriceAriaLabel = isPromoActive
    ? `${promoAnnualMembershipPrice} (reg. ${annualMembershipPrice})`
    : annualMembershipPrice

  const getGiftCopy = () => {
    if (isMembership) {
      if (cardChoice === GiftPlanType.GROCERY_YEARLY) {
        return 'Annual Membership'
      }
      return '6 month Memberhsip'
    }
    return `$${cardChoice} gift card for ${recipientName}`
  }

  const handleConfirm = () => {
    trackElementClicked(
      {
        type: 'button',
        content: 'add_payment',
        message_goal: `gift_card_amount${cardChoice}`,
        next_location: routes.SECURE_ADD_GIFT_CARD_PAYMENT.trackingName,
      },
      {
        sendImmediately: true,
      },
    )
    router.push(`${routes.SECURE_ADD_GIFT_CARD_PAYMENT.url}${upsellChecked ? '?upsell=true' : ''}`)
  }

  const handleUpsellClick = () => {
    setUpsellChecked(!upsellChecked)
    trackElementClicked({
      display_sublocation: 'order_overview',
      content: `add_on_membership_${upsellMembershipCost}`,
      message_goal: 'make_gift_extra_membership_upsell',
      type: 'box',
    })
  }

  useEffect(() => {
    trackElementViewed({
      display_sublocation: 'order_overview',
      content: 'gift_order_overview',
      type: 'modal',
    })
  }, [])

  return (
    <Modal contentLabel="Gift card order overview" onRequestClose={onClose}>
      <Header onRequestClose={onClose} />
      <Content modalTitle="Order overview" hasHeader titleProps={{ as: 'h2' }}>
        <Column padding={{ base: ['lg', 0, 0], md: ['xs', 0, 0] }} spacing={{ base: 'sm' }}>
          <Stack
            direction={{ base: 'column-reverse', md: 'row' }}
            justify={{ md: 'space-between' }}
            align={{ base: 'start' }}
          >
            <RecipientInfo>
              <GiftTitleCopy size="md" as="h3" isPII>
                {getGiftCopy()}
              </GiftTitleCopy>
              <Body size="md" variant="secondary" isPII>
                To: {recipientName}
              </Body>
              <RecipientEmail size="md" variant="secondary" isPII>
                Email: {recipientEmail}
              </RecipientEmail>
              {!!personalMessage && (
                <Body size="md" variant="secondary" isPII>
                  {personalMessage}
                </Body>
              )}
            </RecipientInfo>
            <MobileImage width={84} height={48} src={giftImage.src} alt={giftImage.alt} />
            <DesktopImage width={224} height={128} src={giftImage.src} alt={giftImage.alt} />
          </Stack>
          <Divider />
          {!!showUpsellSkeleton && (
            <>
              <Skeleton width="100%" height={80} />
              <Divider />
            </>
          )}
          {!!showUpsell && (
            <>
              <UpsellWrapper
                direction={{ base: 'column', md: 'row' }}
                spacing={{ base: 'sm', md: '4xl' }}
                justify={{ base: 'start', md: 'center' }}
              >
                <TileGroup name="Upsell Membership">
                  <UpsellCheckBox
                    inputElement={(props) => (
                      <Checkbox
                        checked={upsellChecked}
                        size="md"
                        aria-label={`Make this gift extra special by adding an Annual Membership gift card for ${MembershipPriceAriaLabel}`}
                        {...props}
                        onChange={handleUpsellClick}
                      />
                    )}
                    header={renderMembershipPriceCopy()}
                  />
                </TileGroup>
                <Column>
                  {[
                    '$0 delivery fee on orders $35+*',
                    'Add favorite shoppers',
                    'Exclusive deals',
                  ].map((perk) => (
                    <PerksRow key={perk} spacing="md">
                      <PerksCheckIcon size="md" />
                      <Body size="sm" variant="secondary">
                        {perk}
                      </Body>
                    </PerksRow>
                  ))}
                </Column>
              </UpsellWrapper>
              <Divider />
              {!!upsellChecked && (
                <Body variant="secondary" size="md">
                  Total order amount: <strong>${upsellTotal}</strong>
                </Body>
              )}
            </>
          )}
          <Stack
            direction={{ base: 'column', md: 'row' }}
            justify={{ md: 'space-between' }}
            spacing={{ base: 'xxl' }}
          >
            <Column>
              <Body size="md" variant="secondary" isPII>
                From: {senderName}
              </Body>
              <Body size="md" variant="secondary" isPII>
                Email: {senderEmail}
              </Body>
            </Column>
            <Row spacing="sm" justify={{ base: 'center' }}>
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={handleConfirm}>Confirm</Button>
            </Row>
          </Stack>
        </Column>
        {!!showUpsell && (
          <Terms size="sm">
            *Deliveries under $35 with a membership will incur a fee. See{' '}
            <a href={LEGAL_LINKS.TERMS_OF_SERVICE} target="_blank" rel="noopener noreferrer">
              Terms of Service.
            </a>
          </Terms>
        )}
      </Content>
    </Modal>
  )
}

const GiftTitleCopy = styled(Headline)`
  padding: 1rem 0;
`
const RecipientInfo = styled(Column)`
  @media ${screenSizes.tablet} {
    max-width: 60%;
  }
`
const RecipientEmail = styled(Body)`
  padding-bottom: 1rem;
`
const Divider = styled(HDivider)`
  @media ${screenSizes.max_mobile} {
    margin: 8px auto;
  }
`
const MobileImage = styled(Image)`
  @media ${screenSizes.tablet} {
    display: none;
  }
`
const DesktopImage = styled(Image)`
  display: none;

  @media ${screenSizes.tablet} {
    display: flex;
  }
`
const CheckBoxDescription = styled(Body)`
  color: ${theme.color.content.action.secondary};

  @media ${screenSizes.tablet} {
    max-width: 20rem;
  }
`

const UpsellCheckBox = styled(Tile)`
  display: flex;
  flex-direction: row-reverse;
  border: none;
  padding: 0;
  border-width: 0;
`
const PerksCheckIcon = styled(PositiveIcon)`
  color: ${theme.color.background.action.secondary_loud};
`

const PerksRow = styled(Row)`
  width: 14rem;
`
const UpsellWrapper = styled(Stack)`
  align-items: start;
`
const Terms = styled(Body)`
  margin-top: 2rem;
`
